"use client";

import { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  if (loading) {
    return <div className="hidden md:flex gap-1">
        <div className="h-9 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <>
      <NavigationMenu viewport={false} className="hidden md:flex">
        <NavigationMenuList>
          {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="group relative inline-flex h-9 items-center justify-center px-4 py-2 text-sm font-medium transition-colors hover:!bg-transparent focus:!bg-transparent hover:text-primary-dark focus:text-primary-dark">
                  {item.label}
                  <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-primary transition-all duration-300 ease-out group-hover:w-full" />
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>)}
        </NavigationMenuList>
      </NavigationMenu>

      <Button variant="ghost" size="icon" className="md:hidden hover:!bg-transparent focus:!bg-transparent" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
        {mobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
      </Button>

      {mobileMenuOpen && <div className="absolute top-full left-0 right-0 bg-background border-b border-border shadow-medium md:hidden">
          <nav className="container mx-auto px-6 py-4">
            <ul className="flex flex-col gap-2">
              {menuItems.map((item, i) => <li key={item.id} data-index={i}>
                  <Link to={item.href || "#"} className="block py-2 px-4 text-sm font-medium hover:bg-muted rounded-md transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    {item.label}
                  </Link>
                </li>)}
            </ul>
          </nav>
        </div>}
    </>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={`fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border transition-all duration-300 ease-out ${scrolled ? "h-auto md:h-[60px]" : "h-auto md:h-[80px]"}`}>
      <div className="container mx-auto px-6 h-full flex flex-row items-center justify-between !bg-[hsl(0,0%,100%)] py-4 md:py-0">
        <Link to="/" className="flex items-center hover:opacity-90 transition-opacity">
          <img src="https://markjohnstonmotors.ie/wp-content/uploads/2026/02/Automotive-Repair-Shop-Servicing-and-repairs-of-all-makes-of-cars-and-light-commercial-vehicles-Full-diagnostics-NCT-repairs-NCT-checks-Tyres-and-puncture-repairs-1-scaled.png" alt="Mark Johnston Motors Ltd." className="w-1/2 h-auto md:h-[200px] md:w-auto" />
        </Link>

        <MenuProvider menu_id="32" className="flex items-center gap-4">
          <HeaderMenuConsumer />
        </MenuProvider>

        <Button asChild className="hidden md:inline-flex bg-[var(--color-button-red)] text-[var(--color-foreground-button)] hover:bg-[var(--color-button-red-hover)] hover:shadow-medium hover:-translate-y-0.5 active:bg-[var(--color-button-red-active)] active:translate-y-0 transition-all duration-100">
          <Link to="#">Get Quote</Link>
        </Button>
      </div>
    </header>;
}